<template>
  <div
    class="p-4 border border-border flex flex-col gap-4"
    style="border-radius: var(--radius)"
  >
    <div class="flex flex-col gap-2">
      <div
        class="flex justify-between items-center cursor-pointer"
        @click.stop="toggleExpand(group.key)"
      >
        <div class="flex items-center gap-2">
          <span class="text-muted-foreground text-sm font-medium leading-5">
            {{ group.title }}
          </span>

          <div
            v-if="group?.action?.icon"
            class="flex items-center justify-center cursor-pointer"
          >
            <svg-icon
              @click.stop="handleClick(group.action)"
              :name="group.action.icon"
              class="w-5 h-5 text-muted-foreground"
            />
          </div>
        </div>

        <svg-icon
          :name="expanded[group.key] ? 'huge-up' : 'huge-down'"
          class="text-muted-foreground cursor-pointer w-5 h-5"
        />
      </div>
      <slot name="test" />
    </div>

    <div v-show="expanded[group.key]" class="flex flex-col gap-2">
      <div v-if="isAccessLoading" class="flex justify-center items-center py-8">
        <Spinner size="sm" />
      </div>
      <template v-else>
        <div
          v-for="item in group.items"
          :key="item.code"
          class="section-item"
          :class="{ active: isActive(item) }"
          @click="changeService(item)"
        >
          <span class="text-[13px] leading-5">
            {{ item.name }}
          </span>

          <span
            class="ml-auto flex items-center gap-1.5 text-xs font-medium flex-shrink-0"
            :class="getSourceStatusColor(item)"
          >
            {{ getSourceStatusLabel(item) }}
            <Spinner
              v-if="isStepRunning(item)"
              size="sm"
              class="flex-shrink-0"
            />
            <span
              v-else
              class="w-2 h-2 rounded-full flex-shrink-0"
              :class="getSourceDotColor(item)"
            />
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { reactive, onMounted } from "vue";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import { Spinner } from "@/shared/ui/spinner";
import { useServiceQueueStore } from "@/store/serviceQueue";
import { ServiceQueueStepStatus } from "@/shared/config/serviceQueueStepStatuses";

const serviceQueue = useServiceQueueStore();

const props = defineProps({
  group: {
    type: Object,
    required: true,
  },
  activeService: {
    type: String,
    default: null,
  },
  participant: {
    type: Object,
    default: null,
  },
  selectedParticipant: {
    type: Object,
    default: null,
  },
  isAccessLoading: {
    type: Boolean,
    default: false,
  },
  requestedServices: {
    type: Map,
    default: () => new Map(),
  },
});

const emit = defineEmits(["handleClick", "change:service"]);

const expanded = reactive({});

onMounted(() => {
  if (props.group.expanded || props.group.items.length > 0) {
    expanded[props.group.key] = true;
  }
});

function toggleExpand(code) {
  expanded[code] = !expanded[code];
}

function handleClick(val) {
  emit("handleClick", val);
}

function changeService(item) {
  emit("change:service", item);
}

function isActive(item) {
  const isActiveService = item.code === props.activeService;
  if (props.selectedParticipant) {
    return (
      props.participant?.uuid === props.selectedParticipant?.uuid &&
      isActiveService
    );
  }
  return isActiveService;
}

function getQueueStatus(item) {
  if (!props.participant?.uuid) return null;
  return serviceQueue.getStepStatus(props.participant.uuid, item.code);
}

function getRequestStatus(item) {
  if (!props.participant?.uuid) return null;
  const queueStatus = getQueueStatus(item);
  if (
    queueStatus === ServiceQueueStepStatus.SUCCESS ||
    queueStatus === ServiceQueueStepStatus.ERROR
  ) {
    return queueStatus;
  }
  return (
    props.requestedServices.get(props.participant.uuid + ":" + item.code) ||
    null
  );
}

function isStepRunning(item) {
  return getQueueStatus(item) === ServiceQueueStepStatus.RUNNING;
}

function getSourceStatusLabel(item) {
  if (isStepRunning(item)) return "Запрос...";
  const status = getRequestStatus(item);
  if (status === ServiceQueueStepStatus.SUCCESS) return "Получено";
  if (status === ServiceQueueStepStatus.ERROR) return "Ошибка";
  return "";
}

function getSourceStatusColor(item) {
  if (isStepRunning(item)) return "text-blue-600";
  const status = getRequestStatus(item);
  if (status === ServiceQueueStepStatus.SUCCESS) return "text-green-600";
  if (status === ServiceQueueStepStatus.ERROR) return "text-red-600";
  return "text-muted-foreground";
}

function getSourceDotColor(item) {
  const status = getRequestStatus(item);
  if (status === ServiceQueueStepStatus.SUCCESS) return "bg-green-500";
  if (status === ServiceQueueStepStatus.ERROR) return "bg-red-500";
  return "";
}
</script>

<style scoped lang="scss">
.section-item {
  @apply flex items-center gap-4 text-foreground border transition cursor-pointer;
  padding: 8px 16px;
  border-radius: var(--radius);
  border-color: hsl(var(--border));
  background-color: hsl(var(--background));

  &:hover {
    background-color: hsl(var(--muted));
  }

  &.active {
    background-color: hsl(var(--muted));
    border-color: hsl(var(--foreground));
  }
}
</style>
